import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendOtp, verifyOtp, LoginVendorOption } from '../api/client';
import { useAuth } from '../hooks/useAuth';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [rcCode] = useState(searchParams.get('rc') ?? 'C01');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // v0.13: メアド重複時の業者選択（複数の登録完了業者が同一メアドに紐づく場合）
  const [vendorChoices, setVendorChoices] = useState<LoginVendorOption[] | null>(null);
  const [token, setToken] = useState('');

  // メールリンクからの遷移時：emailがURLにあれば自動でOTP送信
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      handleSendOtp(emailFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendOtp = async (targetEmail?: string) => {
    const mailTo = targetEmail ?? email;
    if (!mailTo) return;
    setLoading(true);
    setError('');
    try {
      await sendOtp(mailTo, rcCode);
      setOtpSent(true);
    } catch (e: any) {
      setError(e.response?.data?.message ?? '送信に失敗しました。メールアドレスを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const proceed = (v: LoginVendorOption, t: string = token) => {
    login({ email, vendorCompanyCode: v.companyCode, receivingCompanyCode: v.receivingCompanyCode, isRegistered: true, token: t });
    // redirect は画面内の決まった行き先だけ許可（外部URLへの誘導を防ぐ）
    const redirect = searchParams.get('redirect');
    const allowed = ['invoice', 'inquiry', 'change-contact'];
    navigate(redirect && allowed.includes(redirect) ? `/${redirect}` : '/invoice');
  };

  const handleVerify = async () => {
    if (!otp) return;
    setLoading(true);
    setError('');
    try {
      const res = await verifyOtp(email, otp);
      const vendors = res.data.vendors ?? [];
      setToken(res.data.token);
      if (vendors.length === 0) {
        // 登録完了業者なし＝未登録 or 招待中。登録はトークン付き招待リンクから行う。
        setError('このメールアドレスで登録が完了した取引先が見つかりません。招待メールのリンクから登録を完了してください。');
        return;
      }
      if (vendors.length === 1) {
        proceed(vendors[0], res.data.token);
      } else {
        // v0.13: 同一メアドに複数の登録完了業者 → どの取引先として利用するか選択させる
        setVendorChoices(vendors);
      }
    } catch (e: any) {
      const msg = e.response?.data?.message ?? '認証コードが違うか、有効期限が切れています。「認証コードを再送する」を押してください。';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>請求書受付システム</h1>
        <p style={styles.subtitle}>取引先ログイン</p>

        {vendorChoices ? (
          <>
            <p style={styles.info}>
              このメールアドレスには複数の取引先が登録されています。<br />
              ログインする取引先を選択してください。
            </p>
            {vendorChoices.map(v => (
              <button key={v.companyCode} style={styles.choiceButton} onClick={() => proceed(v)}>
                <strong>{v.companyName || v.companyCode}</strong>
                <span style={styles.choiceSub}>受領先: {v.receivingCompanyName}</span>
              </button>
            ))}
          </>
        ) : !otpSent ? (
          <>
            {loading ? (
              <p style={styles.info}>認証コードを送信しています...</p>
            ) : (
              <>
                <label style={styles.label}>メールアドレス</label>
                <input
                  style={styles.input}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="example@company.com"
                  onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                />
                {error && <p style={styles.error}>{error}</p>}
                <button style={styles.button} onClick={() => handleSendOtp()} disabled={loading || !email}>
                  認証コードを送信
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <p style={styles.info}>
              <strong>{email}</strong> に6桁の認証コードを送信しました。<br />
              有効期限は30分です。
            </p>
            <label style={styles.label}>認証コード</label>
            <input
              style={{ ...styles.input, letterSpacing: '0.3em', fontSize: 24, textAlign: 'center' }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              autoFocus
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={styles.button} onClick={handleVerify} disabled={loading || otp.length !== 6}>
              {loading ? '確認中...' : 'ログイン'}
            </button>
            <button style={styles.linkButton} onClick={() => handleSendOtp()} disabled={loading}>
              認証コードを再送する
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' },
  card: { background: '#fff', borderRadius: 12, padding: 40, width: 400, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  title: { margin: 0, fontSize: 22, color: '#1a365d', textAlign: 'center' },
  subtitle: { textAlign: 'center', color: '#718096', marginTop: 4, marginBottom: 28 },
  label: { display: 'block', fontWeight: 600, marginBottom: 6, color: '#2d3748' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 16, boxSizing: 'border-box' },
  button: { width: '100%', marginTop: 16, padding: '12px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, cursor: 'pointer', fontWeight: 600 },
  linkButton: { width: '100%', marginTop: 8, padding: '10px', background: 'none', color: '#2b6cb0', border: '1px solid #2b6cb0', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  error: { color: '#e53e3e', fontSize: 14, marginTop: 8 },
  info: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, padding: 12, fontSize: 14, color: '#2c5282', lineHeight: 1.6, marginBottom: 16 },
  choiceButton: { width: '100%', marginBottom: 10, padding: '12px 14px', background: '#fff', color: '#2d3748', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: 15, cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4 },
  choiceSub: { fontSize: 12, color: '#718096' },
};
